import React, { useCallback, useEffect, useState } from "react";
import { Box, CircularProgress, Paper } from "@mui/material";
import TaskItem from "../../../containers/TaskItems";
import CommonNavigator from "../../../components/common/CommonNavigator";
import Layout from "../../../components/layout/Layout";
import { CustomAdminLayout } from "../../../components/layout/CustomAdminLayout";
import CustomDrawer from "../../../components/layout/CustomDrawer";
import FilterPanel from "../../../components/filters/FilterPanel";
import TodoItemsDialog from "./TodoItemsDialog";
import {
    useGetTodoItemsQuery,
    useGetUserListQuery,
} from "../../../redux/service/modules/sales";
import Loader from "../../../components/common/loader";
import NoDataAvailable from "../../../components/common/NoDataAvailable";
import CustomToggle from "../../../components/common/CustomToggle";
import { useSelector } from "react-redux";

const ToDoItems = () => {
    const dateRange = useSelector((state) => state.date);

    const [filterDetails, setFilterDetails] = useState({
        appliedFilters: {
            recipient: [],
            created_by: [],
            status: "pending",
            start_date: dateRange?.startDate,
            end_date: dateRange?.endDate,
        },
        filterCount: 0,
        rawFilters: {},
    });

    const [open, setOpen] = useState(false);
    const [isFilterOpen, toggleFilter] = useState(false);
    const [filters, setFilters] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [allTodoItems, setAllTodoItems] = useState([]);

    const {
        data: todoItems,
        isFetching,
        isLoading,
        error,
    } = useGetTodoItemsQuery({
        ...filterDetails.appliedFilters,
        page,
        page_size: 10,
    });

    const { data: userList } = useGetUserListQuery();
    const [requiredFilters, setRequiredFilters] = useState([]);

    useEffect(() => {
        setFilterDetails((prevDetails) => ({
            ...prevDetails,
            appliedFilters: {
                ...prevDetails.appliedFilters,
                start_date: dateRange?.startDate,
                end_date: dateRange?.endDate,
            },
        }));
    }, [dateRange]);

    useEffect(() => {
        if (userList) {
            setRequiredFilters(["recipient", "created_by"]);
            setFilters([
                {
                    name: "recipient",
                    label: "Recipients",
                    key: "recipient",
                    options: userList.map((user) => ({
                        label: user.username,
                        id: user.id,
                    })),
                    multiSelect: true,
                },
                {
                    name: "createdByName",
                    label: "Created By",
                    key: "created_by",
                    options: userList.map((user) => ({
                        label: user.username,
                        id: user.id,
                    })),
                    multiSelect: true,
                },
            ]);
        }
    }, [userList]);

    useEffect(() => {
        if (todoItems?.data) {
            const newItems = todoItems.data;

            // If it's the first page, reset the list
            if (page === 1) {
                setAllTodoItems(newItems);
            } else {
                // Remove duplicates based on unique id (assuming each item has an 'id' field)
                setAllTodoItems((prevItems) => {
                    const uniqueItems = [
                        ...prevItems,
                        ...newItems.filter(
                            (item) =>
                                !prevItems.some(
                                    (prevItem) => prevItem.id === item.id
                                )
                        ),
                    ];
                    return uniqueItems;
                });
            }

            // Check if there are more items to load
            if (newItems.length < 10) {
                setHasMore(false);
            }
        }
    }, [todoItems, page]);

    const onFilterApply = (transformedFilters, filterCount, rawFilters) => {
        toggleFilter(false);
        setPage(1);
        setHasMore(true);
        setFilterDetails((prevDetails) => ({
            appliedFilters: {
                ...transformedFilters,
                status: prevDetails.appliedFilters.status,
                start_date: prevDetails.appliedFilters.start_date,
                end_date: prevDetails.appliedFilters.end_date,
            },
            filterCount,
            rawFilters,
        }));
    };

    useEffect(() => {
        const scrollableContainer = document.querySelector(
            ".scrollable-container"
        );
        if (!scrollableContainer) return;

        // Ensure that the container has overflow and height set for scrolling
        scrollableContainer.style.overflowY = "auto";
        scrollableContainer.style.maxHeight = "80vh"; // Or set a specific height as needed

        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } =
                scrollableContainer;

            if (
                scrollTop + clientHeight >= scrollHeight - 1 &&
                !isFetching &&
                hasMore
            ) {
                console.log("Fetching next page...");
                setPage((prevPage) => prevPage + 1);
            }
        };

        // Attach the scroll event
        scrollableContainer.addEventListener("scroll", handleScroll);

        // Cleanup listener on unmount
        return () => {
            scrollableContainer.removeEventListener("scroll", handleScroll);
        };
    }, [isFetching, hasMore, page]);

    const SwitchPendingToCompletedToggle = React.memo(() => {
        const onChange = useCallback(
            (val) => {
                setPage(1);
                setFilterDetails((prevDetails) => ({
                    ...prevDetails,
                    appliedFilters: {
                        ...prevDetails.appliedFilters,
                        status: val,
                    },
                }));
            },
            [setFilterDetails]
        );
        const options = ["pending", "completed"];
        return (
            <CustomToggle
                options={options}
                onChange={onChange}
                selectedValue={
                    filterDetails?.appliedFilters?.status || options[0]
                }
            />
        );
    });

    let staticContent;
    if (isLoading) {
        staticContent = <Loader />;
    } else if (error) {
        staticContent = "Error";
    } else if (todoItems?.data.length === 0) {
        staticContent = <NoDataAvailable />;
    }

    return (
        <Layout>
            <CommonNavigator title={"To Do Items"} />
            <div className="mt-2">
                <CustomAdminLayout
                    title={""}
                    showFilter
                    filterCount={filterDetails.filterCount}
                    onFilterClick={() => toggleFilter(true)}
                    showCreateButton
                    showToggleSwitchElm={true}
                    toggleSwitchElm={<SwitchPendingToCompletedToggle />}
                    createButtonText={"Add To Do Item"}
                    createOnClick={() => setOpen(true)}
                    height={"auto"}
                    isNotAdmin
                >
                    {staticContent ? (
                        <Box className="h-[calc(100vh-400px)] min-h-[500px]">
                            <div style={styles.staticContent}>
                                {staticContent}
                            </div>
                        </Box>
                    ) : (
                        <Paper
                            elevation={0}
                            className="scrollable-container p-4"
                            sx={{
                                height: "100%",
                                overflowY: "auto",
                                "&::-webkit-scrollbar": {
                                    width: "6px", // Width for vertical scrollbar
                                    height: "6px", // Height for horizontal scrollbar
                                    backgroundColor: "#f5f5f5",
                                },
                                "&::-webkit-scrollbar-thumb": {
                                    borderRadius: "3px",
                                    backgroundColor: "#bdbdbd",
                                },
                                "&::-webkit-scrollbar-track": {
                                    borderRadius: "3px",
                                    backgroundColor: "#f5f5f5",
                                },
                            }}
                        >
                            <>
                                {allTodoItems?.map((task, index) => (
                                    <TaskItem key={index} task={task} />
                                ))}
                            </>
                            {isFetching && (
                                <div className="flex justify-center m-2">
                                    <CircularProgress
                                        size={25}
                                        sx={{ color: "#ED1C24" }}
                                    />
                                </div>
                            )}
                        </Paper>
                    )}
                </CustomAdminLayout>
                <CustomDrawer open={isFilterOpen}>
                    <FilterPanel
                        filtersContent={filters}
                        handlePanelClose={() => toggleFilter(false)}
                        onApply={onFilterApply}
                        appliedFilters={filterDetails.rawFilters}
                    />
                </CustomDrawer>

                <TodoItemsDialog
                    open={open}
                    onClose={() => setOpen(false)}
                    isEdit={false}
                    title={"Add New To Do Item"}
                    ctaText={"ADD ITEM"}
                />
            </div>
        </Layout>
    );
};
export default ToDoItems;

const styles = {
    staticContent: {
        height: "80%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
};
